/**
 * 도메인별 라우트 설정 (Backend API 구조 기준)
 * INCOMPLETE_FRONTEND_STRUCTURE.md 반영: Item, Finance, Space, Knowledge, Appeal, Calendar, Log
 */

export type EventRouteGroup = 'event_definition' | 'event_execution';

/** FL1 — Fields & Layout route segment (per sub-domain Configure) */
export const FIELDS_AND_LAYOUT_SEGMENT = 'fields-and-layout' as const;

export function getFieldsAndLayoutPath(domainTop: string, subPath: string): string {
  return `/${domainTop}/${subPath}/${FIELDS_AND_LAYOUT_SEGMENT}`;
}

export interface DomainSubRoute {
  id: string;
  path: string;
  businessDomain: string;
  title: string;
  /** Prefer over title for nav display when present. */
  label?: string;
  emptyMessage?: string;
  /** BP-RC1 — collection | record | overview | … */
  pageType?: import('./domainRouteConfigSchema').PageType;
  /** When route does not use unified workbench surface */
  nonWorkbenchReason?: string;
  /** Event L0 — Definition vs Execution/Log UI grouping */
  group?: EventRouteGroup;
  /** 레거시 서브도메인 — nav에서 숨김 또는 deprecated 라벨 */
  legacy?: boolean;
  /** ISO date — physical route removal target (E7 sunset; CI via compat-route pattern) */
  removeAfter?: string;
  /** Operator-facing sunset copy for legacy routes */
  sunsetNotice?: string;
  /** Detail route param segment (default `id`). */
  detailRouteParam?: string;
  /** Emit Fields & Layout configure matrix row (default true). */
  fieldsAndLayout?: boolean;
  /** BP-RC1 — hide from sidebar while keeping route (supplier proxy, etc.) */
  menuVisible?: boolean;
  hiddenRouteReason?: import('./domainRouteConfigSchema').HiddenRouteReason;
  /** BP-RC1 — route-level permission keys (conscious hub panels, etc.) */
  permissions?: string[];
  /** Sidebar — admin-only menu leaf (system footer). */
  adminOnly?: boolean;
  /** Route mounted from router/index.tsx — config is menu SSOT only. */
  indexMounted?: boolean;
  /** Resolver guard wrapper (system plane). */
  routeGuard?: 'system-admin' | 'system-audit';
  icon?: string;
  surfaceId?: string;
  recordSurfaceId?: string;
  breadcrumb?: Array<{ label: string; href?: string }>;
}

export const itemSubRoutes: DomainSubRoute[] = [
  {
    id: 'item-dashboard',
    path: 'dashboard',
    businessDomain: 'sellable_item',
    title: '대시보드',
    pageType: 'overview',
    nonWorkbenchReason: 'domain-dashboard',
    fieldsAndLayout: false,
  },
  {
    id: 'item-sellable',
    path: 'sellable',
    businessDomain: 'sellable_item',
    title: '판매 가능 항목',
    emptyMessage: '아직 등록된 판매 가능 항목이 없습니다.',
  },
  {
    id: 'item-service',
    path: 'service',
    businessDomain: 'service_item',
    title: '서비스 항목',
    emptyMessage: '아직 등록된 서비스 항목이 없습니다.',
  },
  {
    id: 'item-raw-material',
    path: 'raw-material',
    businessDomain: 'raw_material_item',
    title: '원자재',
    emptyMessage: '아직 등록된 원자재가 없습니다.',
  },
  {
    id: 'item-consumable',
    path: 'consumable',
    businessDomain: 'consumable_item',
    title: '소모품',
    emptyMessage: '아직 등록된 소모품이 없습니다.',
  },
  {
    id: 'item-asset',
    path: 'asset',
    businessDomain: 'asset_item',
    title: '자산',
    emptyMessage: '아직 등록된 자산이 없습니다.',
  },
];

export const financeAdvancedSubRoutes: DomainSubRoute[] = [
  {
    id: 'finance-advanced-dashboard',
    path: 'dashboard',
    businessDomain: 'legal_entity',
    title: '대시보드',
    pageType: 'overview',
    nonWorkbenchReason: 'domain-dashboard',
    fieldsAndLayout: false,
  },
  {
    id: 'finance-advanced-legal-entity',
    path: 'legal-entity',
    businessDomain: 'legal_entity',
    title: '법인',
    emptyMessage: '등록된 법인이 없습니다.',
  },
  {
    id: 'finance-advanced-currency-rate',
    path: 'currency-rate',
    businessDomain: 'currency_rate',
    title: '환율',
    emptyMessage: '등록된 환율 정보가 없습니다.',
  },
  {
    id: 'finance-advanced-intercompany',
    path: 'intercompany',
    businessDomain: 'intercompany',
    title: '내부거래',
    emptyMessage: '등록된 내부거래가 없습니다.',
  },
];

export const financeSubRoutes: DomainSubRoute[] = [
  {
    id: 'finance-dashboard',
    path: 'dashboard',
    businessDomain: 'finance_state',
    title: '대시보드',
    pageType: 'overview',
    nonWorkbenchReason: 'domain-dashboard',
    fieldsAndLayout: false,
  },
  {
    id: 'finance-state',
    path: 'state',
    businessDomain: 'finance_state',
    title: '재무상태',
    emptyMessage: '아직 등록된 재무 상태가 없습니다.',
  },
  {
    id: 'finance-flow',
    path: 'flow',
    businessDomain: 'finance_flow',
    title: '재무흐름',
    emptyMessage: '아직 등록된 재무 흐름이 없습니다.',
  },
  {
    id: 'finance-budget',
    path: 'budget',
    businessDomain: 'finance_budget',
    title: '예산 관리',
    label: '예산',
    emptyMessage:
      '등록된 예산이 없습니다. 예산을 추가하면 지출·구매전표 실행 시 자동으로 검증됩니다.',
    fieldsAndLayout: false,
  },
  {
    id: 'finance-vouchers',
    path: 'vouchers',
    businessDomain: 'stream_execution',
    title: '전표',
    emptyMessage: '등록된 전표가 없습니다.',
    fieldsAndLayout: false,
  },
  {
    id: 'finance-voucher-entry',
    path: 'voucher/:streamType/new',
    businessDomain: 'stream_execution',
    title: '전표 작성',
    pageType: 'builder',
    permissions: ['stream_execution:create'],
    nonWorkbenchReason: 'voucher-entry',
    fieldsAndLayout: false,
    menuVisible: false,
    hiddenRouteReason: 'internal',
  },
  {
    id: 'finance-receivables',
    path: 'receivables',
    businessDomain: 'finance_state',
    title: '미수금(AR)',
    emptyMessage: '미수금 항목이 없습니다.',
    fieldsAndLayout: false,
  },
  {
    id: 'finance-overdue',
    path: 'overdue',
    businessDomain: 'finance_state',
    title: '연체 고객',
    emptyMessage: '연체 고객이 없습니다.',
    fieldsAndLayout: false,
  },
  {
    id: 'finance-document-detail',
    path: 'documents/:orgDocumentId',
    pageType: 'record',
    businessDomain: 'finance',
    title: '문서 상세',
    permissions: ['finance:read'],
    nonWorkbenchReason: 'finance-document-detail',
    menuVisible: false,
    hiddenRouteReason: 'internal',
    fieldsAndLayout: false,
  },
  {
    id: 'finance-counting-adjustment-create',
    path: 'inventory/counting-adjustment/new',
    businessDomain: 'stream_execution',
    title: '재고 실사 증가 조정 작성',
    pageType: 'builder',
    permissions: ['document:update'],
    nonWorkbenchReason: 'counting-adjustment-create',
    fieldsAndLayout: false,
    menuVisible: false,
    hiddenRouteReason: 'internal',
  },
  {
    id: 'finance-inventory-shrinkage-note-create',
    path: 'inventory/shrinkage-note/new',
    businessDomain: 'stream_execution',
    title: '재고 손실 조정 작성',
    pageType: 'builder',
    permissions: ['document:update'],
    nonWorkbenchReason: 'inventory-shrinkage-note-create',
    fieldsAndLayout: false,
    menuVisible: false,
    hiddenRouteReason: 'internal',
  },
  {
    id: 'finance-inventory-transformation-note-create',
    path: 'inventory/transformation-note/new',
    businessDomain: 'stream_execution',
    title: '재고 변환 작성',
    pageType: 'builder',
    permissions: ['document:update'],
    nonWorkbenchReason: 'inventory-transformation-note-create',
    fieldsAndLayout: false,
    menuVisible: false,
    hiddenRouteReason: 'internal',
  },
  {
    id: 'finance-inventory-transfer-note-create',
    path: 'inventory/transfer-note/new',
    businessDomain: 'stream_execution',
    title: '재고 이동 작성',
    pageType: 'builder',
    permissions: ['document:update'],
    nonWorkbenchReason: 'inventory-transfer-note-create',
    fieldsAndLayout: false,
    menuVisible: false,
    hiddenRouteReason: 'internal',
  },
];

export const spaceSubRoutes: DomainSubRoute[] = [
  {
    id: 'space-dashboard',
    path: 'dashboard',
    businessDomain: 'physical_space',
    title: '대시보드',
    pageType: 'overview',
    nonWorkbenchReason: 'domain-dashboard',
    fieldsAndLayout: false,
  },
  {
    id: 'space-physical',
    path: 'physical',
    businessDomain: 'physical_space',
    title: '물리 공간',
    label: '물리적 공간',
    emptyMessage: '아직 등록된 물리 공간이 없습니다.',
  },
  {
    id: 'space-virtual',
    path: 'virtual',
    businessDomain: 'virtual_space',
    title: '가상 공간',
    label: '가상 공간',
    emptyMessage: '아직 등록된 가상 공간이 없습니다.',
  },
];

export const knowledgeSubRoutes: DomainSubRoute[] = [
  {
    id: 'knowledge-dashboard',
    path: 'dashboard',
    businessDomain: 'knowledge_report',
    title: '대시보드',
    pageType: 'overview',
    nonWorkbenchReason: 'domain-dashboard',
    fieldsAndLayout: false,
  },
  {
    id: 'knowledge-report',
    path: 'report',
    businessDomain: 'knowledge_report',
    title: '보고서',
    emptyMessage: '아직 등록된 보고서가 없습니다.',
  },
  {
    id: 'knowledge-policy',
    path: 'policy',
    businessDomain: 'knowledge_policy',
    title: '정책',
    emptyMessage: '아직 등록된 정책이 없습니다.',
  },
  {
    id: 'knowledge-plan',
    path: 'plan',
    businessDomain: 'knowledge_plan',
    title: '계획',
    emptyMessage: '아직 등록된 계획이 없습니다.',
  },
];

export const appealSubRoutes: DomainSubRoute[] = [
  {
    id: 'appeal-dashboard',
    path: 'dashboard',
    businessDomain: 'marketing_campaign',
    title: '대시보드',
    pageType: 'overview',
    nonWorkbenchReason: 'domain-dashboard',
    fieldsAndLayout: false,
  },
  {
    id: 'appeal-campaign',
    path: 'campaign',
    businessDomain: 'marketing_campaign',
    title: '캠페인',
    emptyMessage: '아직 등록된 캠페인이 없습니다.',
  },
  {
    id: 'appeal-content',
    path: 'content',
    businessDomain: 'content_asset',
    title: '콘텐츠',
    emptyMessage: '아직 등록된 콘텐츠가 없습니다.',
  },
  {
    id: 'appeal-semantic-formation',
    path: 'semantic-formation',
    businessDomain: 'semantic_formation',
    title: '의미 형성',
    emptyMessage: '아직 등록된 의미 형성이 없습니다.',
  },
  {
    id: 'appeal-ephemeral-formation',
    path: 'ephemeral-formation',
    businessDomain: 'ephemeral_formation',
    title: '일시 형성',
    label: '일시 콘텐츠',
    emptyMessage: '아직 등록된 일시 형성이 없습니다.',
  },
  {
    id: 'appeal-ephemeral-performance',
    path: 'ephemeral-performance',
    businessDomain: 'ephemeral_performance',
    title: '일시 수행',
    label: '이벤트·행사',
    emptyMessage: '아직 등록된 일시 수행이 없습니다.',
  },
  {
    id: 'appeal-recurrent-performance',
    path: 'recurrent-performance',
    businessDomain: 'recurrent_performance',
    title: '반복 수행',
    label: '정기 행사',
    emptyMessage: '아직 등록된 반복 수행이 없습니다.',
  },
];

export const calendarSubRoutes: DomainSubRoute[] = [
  {
    id: 'calendar-dashboard',
    path: 'dashboard',
    businessDomain: 'calendar_anchor',
    title: '대시보드',
    pageType: 'overview',
    nonWorkbenchReason: 'domain-dashboard',
    fieldsAndLayout: false,
  },
  {
    id: 'calendar-event',
    path: 'event',
    businessDomain: 'calendar_anchor',
    title: '일정',
    label: '이벤트',
    emptyMessage: '아직 등록된 일정이 없습니다.',
  },
  {
    id: 'calendar-trigger',
    path: 'trigger',
    businessDomain: 'calendar_trigger',
    title: '트리거',
    emptyMessage: '아직 등록된 트리거가 없습니다.',
  },
  {
    id: 'calendar-cycle',
    path: 'cycle',
    businessDomain: 'calendar_cycle',
    title: '리듬',
    emptyMessage: '아직 등록된 캘린더 리듬이 없습니다.',
  },
];

export const manufacturingSubRoutes: DomainSubRoute[] = [
  {
    id: 'manufacturing-dashboard',
    path: 'dashboard',
    businessDomain: 'manufacturing_bom',
    title: '대시보드',
    pageType: 'overview',
    nonWorkbenchReason: 'domain-dashboard',
    fieldsAndLayout: false,
  },
  {
    id: 'manufacturing-bom',
    path: 'bom',
    businessDomain: 'manufacturing_bom',
    title: '자재명세서(BOM)',
    label: '자재명세',
    emptyMessage: '아직 등록된 BOM이 없습니다.',
  },
  {
    id: 'manufacturing-production',
    path: 'production-order',
    businessDomain: 'production_order',
    title: '생산 지시',
    emptyMessage: '아직 등록된 생산 지시가 없습니다.',
  },
  {
    id: 'manufacturing-quality',
    path: 'quality-inspection',
    businessDomain: 'quality_inspection',
    title: '품질 검수',
    label: '품질 검사',
    emptyMessage: '아직 등록된 품질 검수가 없습니다.',
  },
  {
    id: 'manufacturing-synthetic-pilot',
    path: 'synthetic-pilot',
    businessDomain: 'quality_inspection',
    title: 'Synthetic pilot',
    label: '합성 리허설',
    pageType: 'overview',
    nonWorkbenchReason: 'synthetic-pilot-dashboard',
    fieldsAndLayout: false,
    menuVisible: false,
    hiddenRouteReason: 'internal',
  },
];

/** Event — L0 Definition (Operation-Flow · legacy stream) */
export const eventDefinitionSubRoutes: DomainSubRoute[] = [
  {
    id: 'flow-list',
    path: 'flow-definition',
    businessDomain: 'flow_definition',
    title: 'Flow 정의 (Operation-Flow)',
    label: '흐름 정의 목록',
    group: 'event_definition',
    emptyMessage: '아직 등록된 Flow 정의가 없습니다.',
  },
  {
    id: 'flow-streams-legacy',
    path: 'stream-definition',
    businessDomain: 'stream_definition',
    title: 'Stream 정의 (레거시)',
    label: '흐름 정의 (v1)',
    group: 'event_definition',
    legacy: true,
    menuVisible: false,
    hiddenRouteReason: 'deprecated-legacy',
    removeAfter: '2027-07-01',
    sunsetNotice: 'v2 SSOT: flow_definition. 본 화면은 read/migrate 전용입니다.',
    emptyMessage: '등록된 레거시 흐름 정의가 없습니다. 신규 업무 흐름은 Flow 정의에서 만드세요.',
  },
  {
    id: 'flow-action-binding',
    path: 'action-binding',
    businessDomain: 'action_binding',
    title: 'Action 연결',
    group: 'event_definition',
    emptyMessage: '아직 등록된 Action 연결이 없습니다.',
  },
];

/** Event — Execution / Log */
export const eventExecutionSubRoutes: DomainSubRoute[] = [
  {
    id: 'flow-execution',
    path: 'flow-execution',
    businessDomain: 'flow_execution',
    title: 'Flow 실행 기록',
    group: 'event_execution',
    emptyMessage: '아직 Flow 실행 기록이 없습니다.',
  },
  {
    id: 'stream-execution',
    path: 'stream-execution',
    businessDomain: 'stream_execution',
    title: 'Stream 실행 기록 (레거시)',
    label: '흐름 실행 기록 (v1)',
    group: 'event_execution',
    legacy: true,
    menuVisible: false,
    hiddenRouteReason: 'deprecated-legacy',
    removeAfter: '2027-07-01',
    sunsetNotice: 'v2 SSOT: flow_execution. 본 화면은 read/migrate 전용입니다.',
    emptyMessage: '등록된 레거시 실행 기록이 없습니다. 신규 기록은 Flow 실행 기록에서 확인하세요.',
  },
  {
    id: 'log-flow-events',
    path: 'flow-event',
    businessDomain: 'flow_event',
    title: 'Flow 이벤트',
    label: '흐름 이벤트',
    group: 'event_execution',
    emptyMessage: '아직 Flow 이벤트가 없습니다.',
  },
  {
    id: 'log-stream-events',
    path: 'stream-event',
    businessDomain: 'stream_event',
    title: 'Stream 이벤트 (레거시)',
    label: '흐름 이벤트 (v1)',
    group: 'event_execution',
    legacy: true,
    menuVisible: false,
    hiddenRouteReason: 'deprecated-legacy',
    removeAfter: '2027-07-01',
    sunsetNotice: 'v2 SSOT: flow_execution / OperationExecution. read only.',
    emptyMessage: '등록된 레거시 이벤트가 없습니다. 신규는 Flow 실행·이벤트에서 확인하세요.',
  },
];

export const eventSubRoutes: DomainSubRoute[] = [
  ...eventDefinitionSubRoutes,
  ...eventExecutionSubRoutes,
];

/** WB4.3-S1 Inc 5c — entity PTG reference slice (no F&L configure routes). */
export const entitySubRoutes: DomainSubRoute[] = [
  {
    id: 'entity-internal',
    path: 'internal',
    businessDomain: 'internal_entity',
    title: '내부 엔티티',
    label: '내부 조직',
    fieldsAndLayout: false,
  },
  {
    id: 'entity-external',
    path: 'external',
    businessDomain: 'external_entity',
    title: '외부 엔티티',
    label: '외부 관계',
    detailRouteParam: 'entityId',
    fieldsAndLayout: false,
  },
  {
    id: 'entity-external-create',
    path: 'external/create',
    businessDomain: 'external_entity',
    title: '외부 관계 등록',
    pageType: 'builder',
    nonWorkbenchReason: 'entity-create-form',
    fieldsAndLayout: false,
  },
  {
    id: 'entity-supplier',
    path: 'supplier',
    businessDomain: 'supplier',
    title: '공급처',
    pageType: 'collection',
    menuVisible: false,
    hiddenRouteReason: 'internal',
    fieldsAndLayout: false,
  },
  {
    id: 'entity-user',
    path: 'user',
    businessDomain: 'user_entity',
    title: '사용자 엔티티',
    label: '사용자',
    fieldsAndLayout: false,
  },
];

/** WB4.3-S1 Inc 5b — warehouse hub (picking list). */
export const warehouseSubRoutes: DomainSubRoute[] = [
  {
    id: 'warehouse-picking-list',
    path: 'picking-list',
    businessDomain: 'warehouse_picking_list',
    title: '피킹 리스트',
    emptyMessage: '출고 대기 항목이 없습니다.',
    fieldsAndLayout: false,
  },
  {
    id: 'warehouse-receiving',
    path: 'receiving',
    businessDomain: 'warehouse_receiving',
    title: '입고 큐',
    emptyMessage: '입고 대기 작업이 없습니다.',
    fieldsAndLayout: false,
  },
  {
    id: 'warehouse-putaway',
    path: 'putaway',
    businessDomain: 'warehouse_putaway',
    title: '적치 작업',
    emptyMessage: '적치 대기 작업이 없습니다.',
    fieldsAndLayout: false,
  },
  {
    id: 'warehouse-ops-board',
    path: 'ops-board',
    businessDomain: 'warehouse_ops_board',
    title: '창고 운영 보드',
    emptyMessage: '운영 보드 항목이 없습니다.',
    fieldsAndLayout: false,
    pageType: 'overview',
    nonWorkbenchReason: 'role-composition-ops-board',
  },
];

/** WB4.3-S1 Inc 5b — inventory hub (shortage aggregation). */
export const inventorySubRoutes: DomainSubRoute[] = [
  {
    id: 'inventory-shortage',
    path: 'shortage',
    businessDomain: 'inventory_shortage',
    title: '재고 부족',
    label: '품절·부족',
    emptyMessage: '부족 재고 항목이 없습니다.',
    fieldsAndLayout: false,
  },
  {
    id: 'inventory-stock',
    path: 'stock',
    businessDomain: 'inventory_stock',
    title: '재고 현황',
    emptyMessage: '등록된 재고 항목이 없습니다.',
    fieldsAndLayout: false,
  },
  {
    id: 'inventory-movements',
    path: 'movements',
    businessDomain: 'inventory_movement',
    title: '재고 이동 이력',
    label: '재고 이동',
    emptyMessage: '이동 이력이 없습니다.',
    fieldsAndLayout: false,
  },
  {
    id: 'inventory-adjustments',
    path: 'adjustments',
    businessDomain: 'inventory_adjustment',
    title: '재고 조정',
    emptyMessage: '조정 이력이 없습니다.',
    fieldsAndLayout: false,
  },
  {
    id: 'inventory-exception-resolution',
    path: 'exception-resolution',
    businessDomain: 'inventory_shortage',
    title: '재고 예외 해소',
    label: '예외 해소',
    pageType: 'overview',
    nonWorkbenchReason: 'inventory-exception-flow-adoption',
    fieldsAndLayout: false,
    menuVisible: true,
  },
];

/** Conscious hub — tool routes stay outside WorkbenchContract (nonWorkbenchReason). */
export const consciousSubRoutes: DomainSubRoute[] = [
  {
    id: 'conscious-monitor',
    path: 'monitor',
    businessDomain: 'conscious_alert',
    title: 'Monitor',
    pageType: 'overview',
    permissions: ['conscious_alert:read'],
    nonWorkbenchReason: 'conscious-redirect',
    fieldsAndLayout: false,
    menuVisible: false,
    hiddenRouteReason: 'redirect',
  },
  {
    id: 'conscious-alerts',
    path: 'alerts',
    businessDomain: 'conscious_alert',
    title: '알림',
    label: '경보',
    pageType: 'queue',
    permissions: ['conscious_alert:read'],
    nonWorkbenchReason: 'conscious-hub-panel',
    fieldsAndLayout: false,
  },
  {
    id: 'conscious-suggestions',
    path: 'suggestions',
    businessDomain: 'conscious_suggestion',
    title: '제안',
    pageType: 'queue',
    permissions: ['conscious_suggestion:read'],
    nonWorkbenchReason: 'conscious-hub-panel',
    fieldsAndLayout: false,
  },
  {
    id: 'conscious-memory',
    path: 'memory',
    businessDomain: 'conscious_memory',
    title: '기억',
    label: '조직 기억',
    pageType: 'queue',
    permissions: ['conscious_memory:read'],
    nonWorkbenchReason: 'conscious-hub-panel',
    fieldsAndLayout: false,
  },
  {
    id: 'conscious-ask',
    path: 'ask',
    businessDomain: 'conscious_wisdom',
    title: '질문',
    label: '지혜 질의',
    pageType: 'builder',
    permissions: ['conscious_wisdom:read'],
    nonWorkbenchReason: 'conscious-tool',
    fieldsAndLayout: false,
  },
  {
    id: 'conscious-brain',
    path: 'brain',
    businessDomain: 'conscious_brain',
    title: 'Brain',
    pageType: 'builder',
    permissions: ['conscious_brain:read'],
    nonWorkbenchReason: 'conscious-tool',
    fieldsAndLayout: false,
  },
  {
    id: 'conscious-wisdom',
    path: 'wisdom',
    businessDomain: 'conscious_wisdom',
    title: 'Wisdom',
    label: '지혜 인사이트',
    pageType: 'builder',
    permissions: ['conscious_wisdom:read'],
    nonWorkbenchReason: 'conscious-tool',
    fieldsAndLayout: false,
  },
  {
    id: 'conscious-stream',
    path: 'stream',
    businessDomain: 'conscious_stream',
    title: 'Stream',
    label: '실시간 흐름 인식',
    pageType: 'builder',
    permissions: ['conscious_stream:read'],
    nonWorkbenchReason: 'conscious-tool',
    fieldsAndLayout: false,
  },
  {
    id: 'conscious-learning',
    path: 'learning',
    businessDomain: 'conscious_learning',
    title: 'Learning',
    label: '학습 루프',
    pageType: 'builder',
    permissions: ['conscious_learning:read'],
    nonWorkbenchReason: 'conscious-tool',
    fieldsAndLayout: false,
  },
  {
    id: 'conscious-pattern',
    path: 'pattern',
    businessDomain: 'conscious_pattern',
    title: '패턴 분석',
    pageType: 'builder',
    permissions: ['conscious_pattern:read'],
    nonWorkbenchReason: 'conscious-tool',
    fieldsAndLayout: false,
  },
  {
    id: 'conscious-neuro',
    path: 'neuro',
    businessDomain: 'conscious_neuro',
    title: 'Neuro',
    label: '신경',
    pageType: 'builder',
    permissions: ['conscious_neuro:read'],
    nonWorkbenchReason: 'conscious-tool',
    fieldsAndLayout: false,
  },
  {
    id: 'conscious-org-state',
    path: 'org-state',
    businessDomain: 'conscious_org_state',
    title: 'Org State',
    label: '조직 상태',
    pageType: 'builder',
    permissions: ['conscious_org_state:read'],
    nonWorkbenchReason: 'conscious-tool',
    fieldsAndLayout: false,
  },
  {
    id: 'conscious-quality-situation',
    path: 'quality-situation',
    businessDomain: 'conscious_wisdom',
    title: 'Quality situation',
    label: '품질 상황 해석',
    pageType: 'builder',
    permissions: ['conscious_wisdom:read'],
    nonWorkbenchReason: 'conscious-quality-situation-readonly',
    fieldsAndLayout: false,
    menuVisible: false,
    hiddenRouteReason: 'internal',
  },
];

/** Utility — automation rules (production nav; workbench utility surface). */
export const automationSubRoutes: DomainSubRoute[] = [
  {
    id: 'automation-root',
    path: '',
    businessDomain: 'flow_definition',
    title: '자동화',
    label: '자동화',
    pageType: 'collection',
    permissions: ['flow_definition:read'],
    nonWorkbenchReason: 'utility-automation',
    menuVisible: false,
    fieldsAndLayout: false,
    areaId: 'automation',
  },
];

/** Utility — data export (data nav; workbench utility surface). */
export const exportSubRoutes: DomainSubRoute[] = [
  {
    id: 'export-root',
    path: '',
    businessDomain: 'flow_definition',
    title: '데이터보내기',
    label: '데이터보내기',
    pageType: 'collection',
    permissions: ['flow_definition:read'],
    nonWorkbenchReason: 'utility-export',
    menuVisible: false,
    fieldsAndLayout: false,
    areaId: 'configure',
  },
];

/** Flow shell — workbench collections + TL-8 builder/editor utility pages. */
export const flowSubRoutes: DomainSubRoute[] = [
  {
    id: 'flow-dashboard',
    path: 'dashboard',
    businessDomain: 'flow_definition',
    title: '대시보드',
    pageType: 'overview',
    nonWorkbenchReason: 'domain-dashboard',
    menuVisible: false,
    fieldsAndLayout: false,
  },
  {
    id: 'flow-new',
    path: 'new',
    businessDomain: 'flow_definition',
    title: '새 업무 흐름',
    label: '새 업무 흐름',
    pageType: 'builder',
    nonWorkbenchReason: 'flow-new-entry',
    fieldsAndLayout: false,
  },
  {
    id: 'flow-executions-detail',
    path: 'executions/:id',
    businessDomain: 'flow_execution',
    title: '실행 상세',
    pageType: 'record',
    nonWorkbenchReason: 'flow-execution-detail',
    menuVisible: false,
    hiddenRouteReason: 'internal',
    fieldsAndLayout: false,
  },
  {
    id: 'flow-definitions-new',
    path: 'definitions/new',
    businessDomain: 'flow_definition',
    title: '흐름 정의 생성',
    pageType: 'builder',
    nonWorkbenchReason: 'flow_utility',
    menuVisible: false,
    fieldsAndLayout: false,
  },
  {
    id: 'flow-definitions-detail',
    path: 'definitions/:flowDefinitionId',
    businessDomain: 'flow_definition',
    title: '흐름 정의 편집',
    pageType: 'builder',
    nonWorkbenchReason: 'flow_utility',
    menuVisible: false,
    hiddenRouteReason: 'internal',
    fieldsAndLayout: false,
  },
  {
    id: 'flow-definitions-basic',
    path: 'definitions/basic',
    businessDomain: 'flow_definition',
    title: '기본 흐름 빌더',
    pageType: 'builder',
    nonWorkbenchReason: 'flow_utility',
    menuVisible: false,
    fieldsAndLayout: false,
  },
  {
    id: 'flow-assignments',
    path: 'assignments',
    businessDomain: 'assignment_execution',
    title: '담당 배정',
    label: '담당 배정',
    pageType: 'collection',
    fieldsAndLayout: false,
  },
  {
    id: 'flow-dispatch',
    path: 'dispatch',
    businessDomain: 'assignment_execution',
    title: '출고 대기',
    label: '출고 대기',
    pageType: 'overview',
    nonWorkbenchReason: 'dispatch-hub',
    fieldsAndLayout: false,
  },
  {
    id: 'flow-dispatch-plan-review',
    path: 'dispatch/:planId',
    businessDomain: 'assignment_execution',
    title: '출고 계획 통제 검토',
    label: '출고 계획 검토',
    pageType: 'record',
    nonWorkbenchReason: 'dispatch-controlled-review',
    menuVisible: false,
    hiddenRouteReason: 'internal',
    fieldsAndLayout: false,
  },
  {
    id: 'flow-assignments-detail',
    path: 'assignments/:id',
    businessDomain: 'assignment_execution',
    title: '담당 배정 상세',
    pageType: 'record',
    nonWorkbenchReason: 'flow-assignment-record',
    menuVisible: false,
    hiddenRouteReason: 'internal',
    fieldsAndLayout: false,
  },
  {
    id: 'flow-history',
    path: 'history',
    businessDomain: 'stream_execution',
    title: '실행 이력',
    pageType: 'overview',
    nonWorkbenchReason: 'stream-execution-history',
    menuVisible: false,
    fieldsAndLayout: false,
  },
  {
    id: 'flow-streams',
    path: 'streams',
    businessDomain: 'flow_definition',
    title: '업무 흐름 템플릿',
    label: '업무 흐름 템플릿',
    pageType: 'collection',
    fieldsAndLayout: false,
  },
  {
    id: 'flow-streams-execute',
    path: 'streams/:id/execute',
    businessDomain: 'flow_definition',
    title: '스트림 실행',
    pageType: 'builder',
    nonWorkbenchReason: 'stream-execute',
    menuVisible: false,
    hiddenRouteReason: 'internal',
    fieldsAndLayout: false,
  },
];

/** Utility — approvals inbox (flow footer menu; workbench utility surface). */
export const approvalsSubRoutes: DomainSubRoute[] = [
  {
    id: 'approvals-pending',
    path: 'pending',
    businessDomain: 'flow_execution',
    title: '승인 대기',
    pageType: 'queue',
    permissions: ['flow:approve'],
    nonWorkbenchReason: 'utility-approvals-inbox',
    fieldsAndLayout: false,
  },
  {
    id: 'approvals-history',
    path: 'history',
    businessDomain: 'flow_execution',
    title: '승인 이력',
    pageType: 'collection',
    permissions: ['flow:approve'],
    nonWorkbenchReason: 'utility-approvals-history',
    fieldsAndLayout: false,
  },
  {
    id: 'approvals-assignment-detail',
    path: ':assignmentId',
    businessDomain: 'flow_execution',
    title: '승인 상세',
    pageType: 'record',
    permissions: ['flow:approve'],
    nonWorkbenchReason: 'utility-approval-detail',
    menuVisible: false,
    hiddenRouteReason: 'internal',
    fieldsAndLayout: false,
  },
];

export const logSubRoutes: DomainSubRoute[] = [
  {
    id: 'log-dashboard',
    path: 'dashboard',
    businessDomain: 'flow_event',
    title: '대시보드',
    pageType: 'overview',
    nonWorkbenchReason: 'domain-dashboard',
    fieldsAndLayout: false,
  },
  {
    id: 'log-flow',
    path: 'flow',
    businessDomain: 'flow_event',
    title: '플로우 로그',
    label: '업무 흐름 로그',
    group: 'event_execution',
    emptyMessage: '아직 플로우 로그가 없습니다.',
  },
  {
    id: 'log-stream',
    path: 'stream',
    businessDomain: 'stream_event',
    title: '스트림 로그 (레거시)',
    label: '흐름 실행 로그',
    group: 'event_execution',
    legacy: true,
    menuVisible: false,
    hiddenRouteReason: 'deprecated-legacy',
    removeAfter: '2027-07-01',
    sunsetNotice: 'v2 SSOT: flow_execution. read only. Prefer /log/flow or /event/flow-execution.',
    emptyMessage: '등록된 레거시 로그가 없습니다. 조회 전용입니다.',
  },
];

/** System — footer menu SSOT + workbench shell routes (settings/dashboard remain index-mounted). */
export const systemSubRoutes: DomainSubRoute[] = [
  {
    id: 'system-dashboard',
    path: 'dashboard',
    businessDomain: 'organizational_state',
    title: '홈 대시보드',
    label: '홈 대시보드',
    pageType: 'overview',
    nonWorkbenchReason: 'system-dashboard',
    indexMounted: true,
    fieldsAndLayout: false,
  },
  {
    id: 'system-schema',
    path: 'schema/create',
    businessDomain: 'organizational_state',
    title: '스키마 생성',
    label: '스키마 생성',
    pageType: 'builder',
    nonWorkbenchReason: 'system-schema-create',
    routeGuard: 'system-admin',
    adminOnly: true,
    fieldsAndLayout: false,
  },
  {
    id: 'system-canonical-schema-builder',
    path: 'canonical-schema-builder',
    businessDomain: 'organizational_state',
    title: 'Canonical Schema Builder',
    label: 'Canonical Schema Builder',
    pageType: 'overview',
    nonWorkbenchReason: 'canonical-schema-builder',
    routeGuard: 'system-admin',
    adminOnly: true,
    indexMounted: true,
    fieldsAndLayout: false,
  },
  {
    id: 'system-cross-layer',
    path: 'cross-layer-view',
    businessDomain: 'organizational_state',
    title: '4계층 탐색',
    label: '4계층 탐색',
    pageType: 'overview',
    nonWorkbenchReason: 'system-cross-layer',
    routeGuard: 'system-admin',
    adminOnly: true,
    indexMounted: true,
    fieldsAndLayout: false,
  },
  {
    id: 'system-mail-settings',
    path: 'settings/mail',
    businessDomain: 'organizational_state',
    title: '조직 메일',
    label: '조직 메일',
    pageType: 'builder',
    nonWorkbenchReason: 'system-settings',
    indexMounted: true,
    fieldsAndLayout: false,
  },
  {
    id: 'system-auth-settings',
    path: 'settings/auth',
    businessDomain: 'organizational_state',
    title: '인증 및 초대',
    label: '인증 및 초대',
    pageType: 'builder',
    nonWorkbenchReason: 'system-settings',
    indexMounted: true,
    fieldsAndLayout: false,
  },
  {
    id: 'system-access-keys',
    path: 'settings/access-keys',
    businessDomain: 'organizational_state',
    title: '내 Access Keys',
    label: '내 Access Keys',
    pageType: 'builder',
    nonWorkbenchReason: 'system-settings',
    indexMounted: true,
    fieldsAndLayout: false,
  },
  {
    id: 'system-entity-permissions',
    path: 'settings/internal-entity-permissions',
    businessDomain: 'organizational_state',
    title: '구성원 권한 위임',
    label: '구성원 권한 위임',
    pageType: 'builder',
    nonWorkbenchReason: 'system-settings',
    indexMounted: true,
    fieldsAndLayout: false,
  },
  {
    id: 'system-document-templates',
    path: 'settings/document-templates',
    businessDomain: 'organizational_state',
    title: '증빙 문서 템플릿',
    label: '증빙 문서 템플릿',
    pageType: 'builder',
    nonWorkbenchReason: 'system-settings',
    indexMounted: true,
    fieldsAndLayout: false,
  },
  {
    id: 'system-document-templates-advanced',
    path: 'settings/document-templates/advanced',
    businessDomain: 'organizational_state',
    title: '증빙 문서 고급 빌더',
    pageType: 'builder',
    nonWorkbenchReason: 'system-settings',
    indexMounted: true,
    menuVisible: false,
    hiddenRouteReason: 'internal',
    fieldsAndLayout: false,
  },
  {
    id: 'system-workbench-role-config',
    path: 'settings/workbench-role-config',
    businessDomain: 'organizational_state',
    title: '워크벤치 역할 설정',
    pageType: 'builder',
    nonWorkbenchReason: 'system-settings',
    indexMounted: true,
    menuVisible: false,
    hiddenRouteReason: 'internal',
    fieldsAndLayout: false,
  },
  {
    id: 'system-settings-redirect',
    path: 'settings',
    businessDomain: 'organizational_state',
    title: '설정',
    pageType: 'overview',
    nonWorkbenchReason: 'system-settings-redirect',
    menuVisible: false,
    hiddenRouteReason: 'redirect',
    fieldsAndLayout: false,
  },
  {
    id: 'system-site-join',
    path: 'site-join-applications',
    businessDomain: 'organizational_state',
    title: 'Site 신청 관리',
    label: 'Site 신청 관리',
    pageType: 'collection',
    nonWorkbenchReason: 'system-site-join',
    fieldsAndLayout: false,
  },
  {
    id: 'system-tenant-genesis',
    path: 'tenant-genesis',
    businessDomain: 'organizational_state',
    title: 'Tenant Genesis',
    label: 'Tenant Genesis',
    pageType: 'collection',
    nonWorkbenchReason: 'system-tenant-genesis',
    fieldsAndLayout: false,
  },
  {
    id: 'system-configuration-workbench',
    path: 'configuration-workbench',
    businessDomain: 'organizational_state',
    title: 'Configuration Workbench',
    label: 'Configuration Workbench',
    pageType: 'collection',
    nonWorkbenchReason: 'system-configuration-workbench',
    fieldsAndLayout: false,
  },
  {
    id: 'system-tenant-activation-journey',
    path: 'tenant-activation-journey',
    businessDomain: 'organizational_state',
    title: 'Tenant Activation Journey',
    label: 'Tenant Activation Journey',
    pageType: 'collection',
    nonWorkbenchReason: 'system-tenant-activation-journey',
    routeGuard: 'system-admin',
    adminOnly: true,
    indexMounted: true,
    fieldsAndLayout: false,
  },
  {
    id: 'system-configuration-workbench-detail',
    path: 'configuration-workbench/:revisionId',
    businessDomain: 'organizational_state',
    title: '구성 제안 상세',
    pageType: 'builder',
    nonWorkbenchReason: 'system-configuration-workbench-detail',
    indexMounted: true,
    menuVisible: false,
    hiddenRouteReason: 'internal',
    fieldsAndLayout: false,
  },
  {
    id: 'system-pilot-qualification',
    path: 'pilot-qualification',
    businessDomain: 'organizational_state',
    title: 'Pilot Qualification',
    label: 'Pilot Qualification',
    pageType: 'collection',
    nonWorkbenchReason: 'system-pilot-qualification',
    fieldsAndLayout: false,
  },
  {
    id: 'system-pilot-qualification-detail',
    path: 'pilot-qualification/:qualificationId',
    businessDomain: 'organizational_state',
    title: 'Pilot Qualification Detail',
    pageType: 'builder',
    nonWorkbenchReason: 'system-pilot-qualification-detail',
    indexMounted: true,
    menuVisible: false,
    hiddenRouteReason: 'internal',
    fieldsAndLayout: false,
  },
  {
    id: 'system-pilot-role-surface',
    path: 'pilot-role-surface',
    businessDomain: 'organizational_state',
    title: 'Pilot Role Surface',
    label: 'Pilot Role Surface',
    pageType: 'collection',
    nonWorkbenchReason: 'system-pilot-role-surface',
    fieldsAndLayout: false,
  },
  {
    id: 'system-pilot-field-surface',
    path: 'pilot-field-surface',
    businessDomain: 'organizational_state',
    title: 'Pilot Field Surface',
    label: 'Pilot Field Surface',
    pageType: 'collection',
    nonWorkbenchReason: 'system-pilot-field-surface',
    fieldsAndLayout: false,
  },
  {
    id: 'system-pilot-operator-support',
    path: 'pilot-operator-support',
    businessDomain: 'organizational_state',
    title: 'Pilot Operator Support',
    label: 'Pilot Operator Support',
    pageType: 'collection',
    nonWorkbenchReason: 'system-pilot-operator-support',
    fieldsAndLayout: false,
  },
  {
    id: 'system-adoption-status',
    path: 'adoption-status',
    businessDomain: 'organizational_state',
    title: 'Adoption Status',
    label: 'Adoption Status',
    pageType: 'collection',
    nonWorkbenchReason: 'system-adoption-status',
    fieldsAndLayout: false,
  },
  {
    id: 'system-flow-surface-composition-proof',
    path: 'flow-surface-composition-proof',
    businessDomain: 'organizational_state',
    title: 'Flow Surface Composition Proof',
    pageType: 'collection',
    nonWorkbenchReason: 'system-flow-surface-composition-proof',
    menuVisible: false,
    hiddenRouteReason: 'internal',
    fieldsAndLayout: false,
  },
  {
    id: 'system-operation-composition-builder',
    path: 'operation-composition-builder',
    businessDomain: 'organizational_state',
    title: 'Operation Composition Builder',
    pageType: 'collection',
    nonWorkbenchReason: 'system-operation-composition-builder',
    menuVisible: true,
    fieldsAndLayout: false,
  },
  {
    id: 'system-remaining-operational-flow-adoption-proof',
    path: 'remaining-operational-flow-adoption-proof',
    businessDomain: 'organizational_state',
    title: 'Remaining Operational Flow Adoption Proof',
    pageType: 'collection',
    nonWorkbenchReason: 'system-remaining-operational-flow-adoption-proof',
    menuVisible: false,
    hiddenRouteReason: 'internal',
    fieldsAndLayout: false,
  },
  {
    id: 'system-produce-inventory-flow-adoption-proof',
    path: 'produce-inventory-flow-adoption-proof',
    businessDomain: 'organizational_state',
    title: 'Produce Inventory Flow Adoption Proof',
    pageType: 'collection',
    nonWorkbenchReason: 'system-produce-inventory-flow-adoption-proof',
    menuVisible: false,
    hiddenRouteReason: 'internal',
    fieldsAndLayout: false,
  },
  {
    id: 'system-object-scan-flow-adoption-proof',
    path: 'object-scan-flow-adoption-proof',
    businessDomain: 'organizational_state',
    title: 'Object Scan Flow Adoption Proof',
    pageType: 'collection',
    nonWorkbenchReason: 'system-object-scan-flow-adoption-proof',
    menuVisible: false,
    hiddenRouteReason: 'internal',
    fieldsAndLayout: false,
  },
  {
    id: 'system-document-review-new',
    path: 'document-review/new',
    businessDomain: 'organizational_state',
    title: 'Document Review Create',
    label: 'Document Review Create',
    pageType: 'collection',
    nonWorkbenchReason: 'system-document-review-new',
    fieldsAndLayout: false,
  },
  {
    id: 'system-document-review-inbox',
    path: 'document-review/inbox',
    businessDomain: 'organizational_state',
    title: 'Document Review Inbox',
    label: 'Document Review Inbox',
    pageType: 'collection',
    nonWorkbenchReason: 'system-document-review-inbox',
    fieldsAndLayout: false,
  },
  {
    id: 'system-document-review-detail',
    path: 'document-review/:id',
    businessDomain: 'organizational_state',
    title: 'Document Review Detail',
    pageType: 'collection',
    nonWorkbenchReason: 'system-document-review-detail',
    indexMounted: true,
    menuVisible: false,
    hiddenRouteReason: 'internal',
    fieldsAndLayout: false,
  },
  {
    id: 'system-document-review-support',
    path: 'document-review-support',
    businessDomain: 'organizational_state',
    title: 'Document Review Support',
    label: 'Document Review Support',
    pageType: 'collection',
    nonWorkbenchReason: 'system-document-review-support',
    fieldsAndLayout: false,
  },
  {
    id: 'system-site-studio',
    path: 'site-studio',
    businessDomain: 'organizational_state',
    title: '사이트 스튜디오',
    label: '사이트 스튜디오',
    pageType: 'builder',
    nonWorkbenchReason: 'system-site-studio',
    indexMounted: true,
    fieldsAndLayout: false,
  },
  {
    id: 'system-site-readiness',
    path: 'site-readiness',
    businessDomain: 'organizational_state',
    title: '사이트 준비도',
    label: '사이트 준비도',
    pageType: 'collection',
    nonWorkbenchReason: 'system-site-readiness',
    fieldsAndLayout: false,
  },
  {
    id: 'system-audit',
    path: 'audit',
    businessDomain: 'organizational_state',
    title: '시스템 감사',
    pageType: 'collection',
    nonWorkbenchReason: 'system-audit',
    routeGuard: 'system-audit',
    menuVisible: false,
    hiddenRouteReason: 'internal',
    fieldsAndLayout: false,
  },
];

/** Workspace hub · role boards · operations/tasks shell routes. */
export const workspaceSubRoutes: DomainSubRoute[] = [
  {
    id: 'workspace-hub',
    path: '',
    businessDomain: 'task_execution',
    title: '작업 허브',
    label: '허브',
    pageType: 'overview',
    nonWorkbenchReason: 'workspace-hub',
    indexMounted: true,
    fieldsAndLayout: false,
    areaId: 'today',
  },
  {
    id: 'workspace-experience-sales-order',
    path: 'experience/sales-order',
    businessDomain: 'task_execution',
    title: 'Sales order experience',
    pageType: 'overview',
    nonWorkbenchReason: 'ptl-representative-journey',
    indexMounted: true,
    menuVisible: false,
    hiddenRouteReason: 'internal',
    fieldsAndLayout: false,
  },
  {
    id: 'workspace-experience-supplier-invoice',
    path: 'experience/supplier-invoice',
    businessDomain: 'task_execution',
    title: 'Supplier invoice experience',
    pageType: 'overview',
    nonWorkbenchReason: 'ptl-representative-journey',
    indexMounted: true,
    menuVisible: false,
    hiddenRouteReason: 'internal',
    fieldsAndLayout: false,
  },
  {
    id: 'workspace-experience-collection',
    path: 'experience/collection',
    businessDomain: 'task_execution',
    title: 'Collection experience',
    pageType: 'overview',
    nonWorkbenchReason: 'ptl-representative-journey',
    indexMounted: true,
    menuVisible: false,
    hiddenRouteReason: 'internal',
    fieldsAndLayout: false,
  },
  {
    id: 'workspace-experience-payment',
    path: 'experience/payment',
    businessDomain: 'task_execution',
    title: 'Payment experience',
    pageType: 'overview',
    nonWorkbenchReason: 'ptl-representative-journey',
    indexMounted: true,
    menuVisible: false,
    hiddenRouteReason: 'internal',
    fieldsAndLayout: false,
  },
  {
    id: 'workspace-experience-accounting-exception',
    path: 'experience/accounting-exception',
    businessDomain: 'task_execution',
    title: 'Accounting exception experience',
    pageType: 'overview',
    nonWorkbenchReason: 'ptl-representative-journey',
    indexMounted: true,
    menuVisible: false,
    hiddenRouteReason: 'internal',
    fieldsAndLayout: false,
  },
  {
    id: 'workspace-experience-setup-accounting-policy',
    path: 'experience/setup/accounting-policy',
    businessDomain: 'task_execution',
    title: 'Accounting policy authoring',
    pageType: 'overview',
    nonWorkbenchReason: 'ptl-setup-import-journey',
    indexMounted: true,
    menuVisible: false,
    hiddenRouteReason: 'internal',
    fieldsAndLayout: false,
  },
  {
    id: 'workspace-experience-setup-coa-mapping',
    path: 'experience/setup/coa-mapping',
    businessDomain: 'task_execution',
    title: 'COA mapping import',
    pageType: 'overview',
    nonWorkbenchReason: 'ptl-setup-import-journey',
    indexMounted: true,
    menuVisible: false,
    hiddenRouteReason: 'internal',
    fieldsAndLayout: false,
  },
  {
    id: 'workspace-experience-setup-opening-balance',
    path: 'experience/setup/opening-balance',
    businessDomain: 'task_execution',
    title: 'Opening balance preview',
    pageType: 'overview',
    nonWorkbenchReason: 'ptl-setup-import-journey',
    indexMounted: true,
    menuVisible: false,
    hiddenRouteReason: 'internal',
    fieldsAndLayout: false,
  },
  {
    id: 'workspace-experience-setup-export-manifest',
    path: 'experience/setup/export-manifest',
    businessDomain: 'task_execution',
    title: 'Ecount export manifest binding',
    pageType: 'overview',
    nonWorkbenchReason: 'ptl-setup-import-journey',
    indexMounted: true,
    menuVisible: false,
    hiddenRouteReason: 'internal',
    fieldsAndLayout: false,
  },
  {
    id: 'workspace-experience-setup-packet-review',
    path: 'experience/setup/packet-review',
    businessDomain: 'task_execution',
    title: 'Cutover packet completeness review',
    pageType: 'overview',
    nonWorkbenchReason: 'ptl-setup-import-journey',
    indexMounted: true,
    menuVisible: false,
    hiddenRouteReason: 'internal',
    fieldsAndLayout: false,
  },
  {
    id: 'workspace-assignments',
    path: 'assignments',
    businessDomain: 'task_execution',
    title: '배정 보드',
    label: '배정 보드',
    pageType: 'overview',
    nonWorkbenchReason: 'workspace-assignments-board',
    indexMounted: true,
    fieldsAndLayout: false,
    areaId: 'today',
  },
  {
    id: 'workspace-sales',
    path: 'sales',
    businessDomain: 'task_execution',
    title: '영업 큐',
    pageType: 'overview',
    nonWorkbenchReason: 'workspace-role-board',
    indexMounted: true,
    menuVisible: false,
    hiddenRouteReason: 'internal',
    fieldsAndLayout: false,
  },
  {
    id: 'workspace-warehouse',
    path: 'warehouse',
    businessDomain: 'task_execution',
    title: '창고 운영 보드',
    pageType: 'overview',
    nonWorkbenchReason: 'workspace-role-board',
    indexMounted: true,
    menuVisible: false,
    hiddenRouteReason: 'internal',
    fieldsAndLayout: false,
  },
  {
    id: 'workspace-finance',
    path: 'finance',
    businessDomain: 'task_execution',
    title: '재무 예외 큐',
    pageType: 'overview',
    nonWorkbenchReason: 'workspace-role-board',
    indexMounted: true,
    menuVisible: false,
    hiddenRouteReason: 'internal',
    fieldsAndLayout: false,
  },
  {
    id: 'workspace-collection-dashboard',
    path: 'collection/dashboard',
    businessDomain: 'task_execution',
    title: '수금 관리 대시보드',
    pageType: 'overview',
    nonWorkbenchReason: 'workspace-role-board',
    indexMounted: true,
    menuVisible: false,
    hiddenRouteReason: 'internal',
    fieldsAndLayout: false,
  },
  {
    id: 'workspace-collection-operator-projection',
    path: 'collection/operator-projection/:arEntryId',
    businessDomain: 'task_execution',
    title: '수금 운영자 투영(읽기 전용)',
    pageType: 'overview',
    nonWorkbenchReason: 'workspace-role-board',
    indexMounted: true,
    menuVisible: false,
    hiddenRouteReason: 'internal',
    fieldsAndLayout: false,
  },
  {
    id: 'workspace-collection-enforcement',
    path: 'collection/enforcement',
    businessDomain: 'task_execution',
    title: '수금 Enforcement',
    pageType: 'overview',
    nonWorkbenchReason: 'workspace-role-board',
    indexMounted: true,
    menuVisible: false,
    hiddenRouteReason: 'internal',
    fieldsAndLayout: false,
  },
  {
    id: 'workspace-collection',
    path: 'collection',
    businessDomain: 'task_execution',
    title: '수금 관리 예외 큐',
    pageType: 'overview',
    nonWorkbenchReason: 'workspace-role-board',
    indexMounted: true,
    menuVisible: false,
    hiddenRouteReason: 'internal',
    fieldsAndLayout: false,
  },
  {
    id: 'workspace-admin',
    path: 'admin',
    businessDomain: 'task_execution',
    title: '권한 감사 보드',
    pageType: 'overview',
    nonWorkbenchReason: 'workspace-role-board',
    indexMounted: true,
    menuVisible: false,
    hiddenRouteReason: 'internal',
    fieldsAndLayout: false,
  },
  {
    id: 'workspace-owner',
    path: 'owner',
    businessDomain: 'task_execution',
    title: '경영진 코크핏',
    pageType: 'overview',
    nonWorkbenchReason: 'workspace-role-board',
    indexMounted: true,
    menuVisible: false,
    hiddenRouteReason: 'internal',
    fieldsAndLayout: false,
  },
  {
    id: 'workspace-settings',
    path: 'settings',
    businessDomain: 'task_execution',
    title: '작업 허브 설정',
    pageType: 'builder',
    nonWorkbenchReason: 'workspace-settings',
    indexMounted: true,
    menuVisible: false,
    hiddenRouteReason: 'internal',
    fieldsAndLayout: false,
  },
  {
    id: 'workspace-operations-detail',
    path: 'operations/:id',
    businessDomain: 'task_execution',
    title: '작업 상세',
    pageType: 'record',
    nonWorkbenchReason: 'workspace-operation-detail',
    menuVisible: false,
    hiddenRouteReason: 'internal',
    fieldsAndLayout: false,
  },
  {
    id: 'workspace-tasks-index',
    path: 'tasks',
    businessDomain: 'task_execution',
    title: '내 할 일',
    label: '내 할 일',
    pageType: 'collection',
    nonWorkbenchReason: 'workspace-task-queue',
    menuVisible: true,
    fieldsAndLayout: false,
    areaId: 'workbench',
  },
  {
    id: 'workspace-tasks-detail',
    path: 'tasks/:taskExecutionId',
    businessDomain: 'task_execution',
    title: '할 일 상세',
    pageType: 'record',
    nonWorkbenchReason: 'workspace-task-detail',
    menuVisible: false,
    hiddenRouteReason: 'internal',
    fieldsAndLayout: false,
  },
];

/** @deprecated Use workspaceSubRoutes — kept for import stability during migration. */
export const workspaceOperationsSubRoutes = workspaceSubRoutes.filter((route) =>
  route.path.startsWith('operations/')
);

/** @deprecated Use workspaceSubRoutes */
export const workspaceTaskSubRoutes = workspaceSubRoutes.filter(
  (route) => route.path === 'tasks' || route.path.startsWith('tasks/')
);

/** Settings layer — personal · role · admin mirror of system/settings (index-mounted). */
export const settingsSubRoutes: DomainSubRoute[] = [
  {
    id: 'settings-personal',
    path: 'personal',
    businessDomain: 'organizational_state',
    title: '개인 설정',
    label: '개인 설정',
    pageType: 'builder',
    nonWorkbenchReason: 'settings-personal',
    indexMounted: true,
    menuVisible: false,
    hiddenRouteReason: 'internal',
    fieldsAndLayout: false,
  },
  {
    id: 'settings-role',
    path: 'role',
    businessDomain: 'organizational_state',
    title: '역할 설정',
    label: '역할 설정',
    pageType: 'builder',
    nonWorkbenchReason: 'settings-role',
    indexMounted: true,
    menuVisible: false,
    hiddenRouteReason: 'internal',
    fieldsAndLayout: false,
  },
  {
    id: 'settings-admin-redirect',
    path: 'admin',
    businessDomain: 'organizational_state',
    title: '관리자 설정',
    label: '관리자 설정',
    pageType: 'overview',
    nonWorkbenchReason: 'settings-admin-redirect',
    menuVisible: false,
    hiddenRouteReason: 'redirect',
    indexMounted: true,
    fieldsAndLayout: false,
  },
  {
    id: 'settings-admin-mail',
    path: 'admin/mail',
    businessDomain: 'organizational_state',
    title: '조직 메일',
    label: '조직 메일',
    pageType: 'builder',
    nonWorkbenchReason: 'settings-admin',
    indexMounted: true,
    adminOnly: true,
    menuVisible: false,
    hiddenRouteReason: 'internal',
    fieldsAndLayout: false,
    breadcrumb: [{ label: '관리자 설정', href: '/settings/admin/mail' }],
  },
  {
    id: 'settings-admin-auth',
    path: 'admin/auth',
    businessDomain: 'organizational_state',
    title: '인증 및 초대',
    label: '인증 및 초대',
    pageType: 'builder',
    nonWorkbenchReason: 'settings-admin',
    indexMounted: true,
    adminOnly: true,
    menuVisible: false,
    hiddenRouteReason: 'internal',
    fieldsAndLayout: false,
    breadcrumb: [{ label: '관리자 설정', href: '/settings/admin/mail' }],
  },
  {
    id: 'settings-admin-access-keys',
    path: 'admin/access-keys',
    businessDomain: 'organizational_state',
    title: '내 Access Keys',
    label: '내 Access Keys',
    pageType: 'builder',
    nonWorkbenchReason: 'settings-admin',
    indexMounted: true,
    adminOnly: true,
    menuVisible: false,
    hiddenRouteReason: 'internal',
    fieldsAndLayout: false,
    breadcrumb: [{ label: '관리자 설정', href: '/settings/admin/mail' }],
  },
  {
    id: 'settings-admin-entity-permissions',
    path: 'admin/internal-entity-permissions',
    businessDomain: 'organizational_state',
    title: '구성원 권한 위임',
    label: '구성원 권한 위임',
    pageType: 'builder',
    nonWorkbenchReason: 'settings-admin',
    indexMounted: true,
    adminOnly: true,
    menuVisible: false,
    hiddenRouteReason: 'internal',
    fieldsAndLayout: false,
    breadcrumb: [{ label: '관리자 설정', href: '/settings/admin/mail' }],
  },
  {
    id: 'settings-admin-workbench-role-config',
    path: 'admin/workbench-role-config',
    businessDomain: 'organizational_state',
    title: '워크벤치 역할 설정',
    pageType: 'builder',
    nonWorkbenchReason: 'settings-admin',
    indexMounted: true,
    adminOnly: true,
    menuVisible: false,
    hiddenRouteReason: 'internal',
    fieldsAndLayout: false,
    breadcrumb: [{ label: '관리자 설정', href: '/settings/admin/mail' }],
  },
];

/** Physical access — workspace footer cross-link. */
export const physicalAccessSubRoutes: DomainSubRoute[] = [
  {
    id: 'physical-access-hub',
    path: '',
    businessDomain: 'task_execution',
    title: '현장 스캔',
    label: '현장 스캔',
    pageType: 'overview',
    nonWorkbenchReason: 'physical-access-hub',
    indexMounted: true,
    fieldsAndLayout: false,
  },
  {
    id: 'physical-access-mint',
    path: 'mint',
    businessDomain: 'task_execution',
    title: '현장 스캔 발급',
    pageType: 'builder',
    nonWorkbenchReason: 'physical-access-mint',
    indexMounted: true,
    menuVisible: false,
    hiddenRouteReason: 'internal',
    fieldsAndLayout: false,
  },
];

export const domainRouteConfig = {
  entity: entitySubRoutes,
  item: itemSubRoutes,
  finance: financeSubRoutes,
  'finance-advanced': financeAdvancedSubRoutes,
  space: spaceSubRoutes,
  knowledge: knowledgeSubRoutes,
  appeal: appealSubRoutes,
  calendar: calendarSubRoutes,
  manufacturing: manufacturingSubRoutes,
  event: eventSubRoutes,
  log: logSubRoutes,
  warehouse: warehouseSubRoutes,
  inventory: inventorySubRoutes,
  conscious: consciousSubRoutes,
  approvals: approvalsSubRoutes,
  automation: automationSubRoutes,
  export: exportSubRoutes,
  flow: flowSubRoutes,
  system: systemSubRoutes,
  workspace: workspaceSubRoutes,
  settings: settingsSubRoutes,
  'physical-access': physicalAccessSubRoutes,
} as const;
